import React from "react";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import "mdb-ui-kit/css/mdb.min.css";
import "@fortawesome/fontawesome-free/css/all.min.css";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { Input, Ripple, initMDB } from "mdb-ui-kit";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { useMediaQuery, useTheme } from "@mui/material";
import FooterManual from "./footerManual";
import Notes from "./header";

initMDB({ Input, Ripple, initMDB });

// Constants for Color Palette - RED THEME
const RED = "#DC2626";
const DARK_RED_ACCENT = "#991B1B";
const BLACK_TEXT = "#1F2937";
const GRAY_TEXT = "#4B5563";
const LIGHT_RED_BG = "#FEF2F2";
const EXTRA_LIGHT_RED_HOVER = "#FFF5F5";

const Item = styled(Paper)(({ theme }) => ({
    backgroundColor: "#fff",
    ...theme.typography.body2,
    padding: theme.spacing(1),
    textAlign: "center",
    color: theme.palette.text.secondary,
    ...theme.applyStyles("dark", {
        backgroundColor: "#1A2027",
    }),
}));

// Helper component for creating the data cards
const InfoCard = ({ title, icon, children }) => (
    <Box 
        sx={{ 
            border: `2px solid ${DARK_RED_ACCENT}`, 
            marginTop: '20px',
            borderRadius: "10px",
            overflow: "hidden",
            boxShadow: `0 4px 12px rgba(220, 38, 38, 0.15)`, 
            transition: "transform 0.3s ease-in-out",
            '&:hover': {
                transform: "translateY(-5px)",
                boxShadow: `0 10px 20px rgba(220, 38, 38, 0.25)`,
            }
        }}
    >
        <Box 
            sx={{ 
                backgroundColor: RED, 
                color: "white", 
                padding: "15px 20px",
                borderBottom: `2px solid ${DARK_RED_ACCENT}` 
            }}
        >
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", display: 'flex', alignItems: 'center' }}>
                <i className={`fas fa-${icon}`} style={{ marginRight: '10px' }}></i>
                {title}
            </Typography>
        </Box>
        <Box sx={{ padding: "20px", backgroundColor: "#fff" }}>
            {children}
        </Box>
    </Box>
);

// Helper component for styled list/detail items
const DetailItem = ({ label, value, labelColor, hoverColor }) => (
    <Box 
        sx={{ 
            display: "flex", 
            alignItems: "flex-start",
            padding: "10px 8px",
            borderBottom: "1px solid #E5E7EB",
            '&:last-child': { borderBottom: 'none' },
            '&:hover': { backgroundColor: hoverColor || EXTRA_LIGHT_RED_HOVER }, 
            transition: 'background-color 0.2s'
        }}
    >
        <Typography sx={{ 
            fontWeight: "600", 
            minWidth: { xs: "110px", sm: "140px" }, 
            color: labelColor || RED, 
            fontSize: "14px",
            flexShrink: 0
        }}>
            {label}:
        </Typography>
        <Typography sx={{ color: BLACK_TEXT, flex: 1, fontSize: "14px", lineHeight: "1.4", whiteSpace: "pre-wrap" }}>
            {value}
        </Typography>
    </Box>
);

const ReturnsPolicy = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
    const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));

    return (
        <Box
            className="container-main-AllSeeModels"
            sx={{
                position: "relative",
                width: "100%",
                minHeight: "100vh",
                backgroundColor: "#fff"
            }}
        >
            {/* Header/Notes Component */}
            <Box sx={{ marginBottom: "0%" }}><Notes /></Box>

            {/* Banner Section */}
            <Box
                sx={{
                    position: "relative",
                    width: "100%",
                    height: isMobile ? "25vh" : isTablet ? "35vh" : "50vh",
                    backgroundColor: "black",
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: "hidden",
                }}
            >
                <Box
                    component="img"
                    src="/image/AllSeeModelsBackgroungCar.png" 
                    alt="Automotive Background"
                    sx={{
                        position: "absolute",
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        opacity: 0.35,
                    }}
                />

                <Typography
                    sx={{
                        position: "relative",
                        color: "white",
                        fontWeight: "900",
                        textTransform: "uppercase",
                        letterSpacing: isMobile ? "1px" : isTablet ? "2px" : "4px",
                        fontSize: isMobile ? "24px" : isTablet ? "42px" : "60px",
                        zIndex: 2,
                        textShadow: "0 0 10px rgba(0,0,0,0.8)",
                        textAlign: "center",
                        lineHeight: 1.1,
                        px: "20px",
                    }}
                >
                    Return & Refund Policy
                </Typography>
            </Box>

            {/* Main Content Container */}
            <Box
                sx={{
                    width: isMobile ? "95%" : isTablet ? "92%" : "85%",
                    backgroundColor: "#FFFFFF",
                    margin: "0 auto",
                    marginTop: isMobile ? "-50px" : isTablet ? "-60px" : "-80px",
                    borderRadius: isMobile ? "12px" : isTablet ? "16px" : "20px",
                    padding: isMobile ? "20px 16px" : isTablet ? "32px 24px" : "40px 30px",
                    boxShadow: "0 15px 50px rgba(0, 0, 0, 0.12)",
                    zIndex: 10,
                    position: "relative",
                }}
            >
                {/* Introduction Section */}
                <Box sx={{ textAlign: "center", marginBottom: isMobile ? "35px" : isTablet ? "45px" : "60px" }}>
                    <Typography
                        sx={{
                            fontWeight: "800",
                            color: DARK_RED_ACCENT,
                            marginBottom: isMobile ? "10px" : "12px",
                            textTransform: "uppercase",
                            fontSize: isMobile ? "18px" : isTablet ? "24px" : "32px",
                            lineHeight: 1.3,
                        }}
                    >
                        Final Sale Policy - International Trade
                    </Typography>
                    <Typography
                        sx={{
                            color: GRAY_TEXT,
                            fontSize: isMobile ? "13px" : isTablet ? "14px" : "16px",
                            maxWidth: "900px",
                            margin: "0 auto",
                            lineHeight: "1.7",
                            px: isMobile ? "12px" : "0",
                        }}
                    >
                        Amar Enterprises Co Ltd maintains a strict Final Sale policy for all items shipped globally. As a Japanese exporter specializing in <b>New & Used Cars, Auto Parts, and Scrap</b>, our sales model is based on <b>international trade principles</b> where all sales are final and non-refundable.
                    </Typography>
                </Box>

                {/* Policy Notice Box */}
                <Box 
                    sx={{
                        backgroundColor: LIGHT_RED_BG,
                        border: `2px solid ${RED}`,
                        borderRadius: isMobile ? "8px" : "12px",
                        padding: isMobile ? "15px" : isTablet ? "18px" : "20px",
                        marginBottom: isMobile ? "30px" : isTablet ? "40px" : "50px",
                        textAlign: "center",
                    }}
                >
                    <Typography
                        sx={{
                            fontWeight: "700",
                            color: RED,
                            fontSize: isMobile ? "14px" : "16px",
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: "8px",
                        }}
                    >
                        <i className="fas fa-ban" style={{ fontSize: isMobile ? "16px" : "18px" }}></i>
                        All Sales Are Final - No Claim, No Return Basis
                    </Typography>
                </Box>

                {/* Two-Column Grid for Core Policy & Vehicle Disclaimer */}
                <Box 
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                        gap: isMobile ? "20px" : isTablet ? "24px" : "30px",
                        marginBottom: isMobile ? "25px" : isTablet ? "35px" : "40px"
                    }}
                >
                    {/* Column 1: Fundamental Policy */}
                    <InfoCard title="Fundamental Policy" icon="gavel">
                        <Typography sx={{ color: GRAY_TEXT, fontSize: isMobile ? "13px" : "15px", marginBottom: "15px" }}>
                            Our transactions are dealt with on a <strong>No Claim, No Return</strong> basis. All sales are FINAL and NON-REFUNDABLE for:
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: isMobile ? "12px" : "15px" }}>
                            <Box>
                                <Typography sx={{ fontWeight: "600", color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", marginBottom: "4px" }}>Vehicles</Typography>
                                <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", lineHeight: "1.4" }}>New, Used, and Modified</Typography>
                            </Box>
                            <Box>
                                <Typography sx={{ fontWeight: "600", color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", marginBottom: "4px" }}>Auto Components</Typography>
                                <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", lineHeight: "1.4" }}>Parts, Wheels, Tires</Typography>
                            </Box>
                            <Box>
                                <Typography sx={{ fontWeight: "600", color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", marginBottom: "4px" }}>Materials</Typography>
                                <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", lineHeight: "1.4" }}>Scraps and Raw Materials</Typography>
                            </Box>
                        </Box>
                    </InfoCard>

                    {/* Column 2: Used Vehicle Condition */}
                    <InfoCard title="Used Vehicle Condition Disclaimer" icon="car-side">
                        <Typography sx={{ color: GRAY_TEXT, fontSize: isMobile ? "13px" : "15px", marginBottom: "15px" }}>
                            By purchasing from Amar Enterprises, you understand and agree:
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: isMobile ? "12px" : "15px" }}>
                            <Box>
                                <Typography sx={{ fontWeight: "600", color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", marginBottom: "4px" }}>Condition</Typography>
                                <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", lineHeight: "1.4" }}>Vehicles are USED and described as honestly as possible</Typography>
                            </Box>
                            <Box>
                                <Typography sx={{ fontWeight: "600", color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", marginBottom: "4px" }}>No Guarantees</Typography>
                                <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", lineHeight: "1.4" }}>Not guaranteed to match buyer's expectations or mental image</Typography>
                            </Box>
                            <Box>
                                <Typography sx={{ fontWeight: "600", color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", marginBottom: "4px" }}>No Claims</Typography>
                                <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", lineHeight: "1.4" }}>Cannot claim on vehicle condition upon delivery</Typography>
                            </Box>
                        </Box>
                    </InfoCard>
                </Box>
                
                {/* Non-Refund Reasons - Table-like presentation */}
                <InfoCard title="Reasons for Non-Refund / Non-Return" icon="exclamation-triangle">
                    <Box 
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", sm: "1fr 1.5fr" },
                            gap: "10px",
                            backgroundColor: LIGHT_RED_BG,
                            padding: isMobile ? "10px" : "15px",
                            borderRadius: "5px",
                            marginBottom: "15px"
                        }}
                    >
                        <Typography sx={{ fontWeight: "bold", color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px" }}>Reason Category</Typography>
                        <Typography sx={{ fontWeight: "bold", color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px" }}>Explanation & Policy</Typography>
                    </Box>

                    {[
                        { 
                            category: 'Change of Mind', 
                            explanation: 'Buyer decides they no longer want the item after payment.' 
                        },
                        { 
                            category: 'Minor Defects/Wear', 
                            explanation: 'Damage, rust, wear, or mechanical issues consistent with age and mileage. Sale is strictly "AS-IS, WHERE-IS."' 
                        },
                        { 
                            category: 'Import Compliance', 
                            explanation: 'Failure to check age limits, emission standards, or licensing rules prior to purchase.' 
                        },
                        { 
                            category: 'Transit Damage', 
                            explanation: 'Damage occurring after goods loaded onto vessel in Japan (risk transfers per FOB/CFR/CIF Incoterms). Claims must be filed against marine insurance.' 
                        },
                        { 
                            category: 'Incorrect Parts', 
                            explanation: 'Buyer ordered incorrect part number or fitment without providing chassis number for verification.' 
                        },
                    ].map((item, index) => (
                        <Box 
                            key={index} 
                            sx={{
                                display: "grid",
                                gridTemplateColumns: { xs: "1fr", sm: "1fr 1.5fr" },
                                gap: "10px",
                                padding: isMobile ? "10px" : "15px",
                                borderBottom: index < 4 ? "1px solid #E5E7EB" : 'none',
                                '&:hover': { backgroundColor: LIGHT_RED_BG } 
                            }}
                        >
                            <Typography sx={{ fontWeight: "600", color: RED, fontSize: isMobile ? "12px" : "14px" }}>{item.category}</Typography>
                            <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", lineHeight: "1.4" }}>{item.explanation}</Typography>
                        </Box>
                    ))}
                </InfoCard>
                
                {/* Limited Exception & Contact Section */}
                <Box 
                    sx={{
                        marginTop: isMobile ? "20px" : isTablet ? "25px" : "30px",
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                        gap: isMobile ? "20px" : isTablet ? "24px" : "30px",
                    }}
                >
                    <InfoCard title="Limited Exception to Final Sale" icon="clipboard-check">
                        <Typography sx={{ color: BLACK_TEXT, fontWeight: 'bold', marginBottom: '10px', fontSize: isMobile ? "12px" : "14px" }}>When Refunds/Replacements Are Considered:</Typography>
                        <Typography sx={{ color: GRAY_TEXT, fontSize: isMobile ? "12px" : "14px", marginBottom: "15px", lineHeight: "1.5" }}>
                            Only if Amar Enterprises commits a verifiable internal error, such as sending a completely different item than purchased (wrong model or year).
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                            <Box>
                                <Typography sx={{ fontWeight: "600", color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", marginBottom: "4px" }}>Claim Deadline</Typography>
                                <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", lineHeight: "1.4" }}>Must be submitted in writing within 7 calendar days of goods clearing Japanese Customs (before loading).</Typography>
                            </Box>
                        </Box>
                    </InfoCard>
                    
                    <InfoCard title="Warranty & Governing Law" icon="balance-scale">
                        <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                            <Box>
                                <Typography sx={{ fontWeight: "600", color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", marginBottom: "4px" }}>Express Warranty</Typography>
                                <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", lineHeight: "1.4" }}>Any warranty offered will be detailed exclusively on the Pro-forma Invoice, superseding this general policy.</Typography>
                            </Box>
                            <Box>
                                <Typography sx={{ fontWeight: "600", color: DARK_RED_ACCENT, fontSize: isMobile ? "12px" : "14px", marginBottom: "4px" }}>Dispute Resolution</Typography>
                                <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", lineHeight: "1.4" }}>All disputes are governed by Japan Jurisdiction as outlined in our Terms and Conditions.</Typography>
                            </Box>
                        </Box>
                        <Box sx={{ marginTop: "15px", padding: isMobile ? "12px" : "15px", backgroundColor: LIGHT_RED_BG, borderRadius: "5px" }}>
                            <Typography sx={{ color: BLACK_TEXT, fontWeight: 'bold', marginBottom: '8px', fontSize: isMobile ? "12px" : "14px" }}>Contact for Claims:</Typography>
                            <Typography sx={{ color: GRAY_TEXT, fontSize: isMobile ? "12px" : "14px", marginBottom: "4px" }}>Email: sales@amar.co.jp</Typography>
                            <Typography sx={{ color: GRAY_TEXT, fontSize: isMobile ? "12px" : "14px" }}>Mobile: +81-48-793-3911</Typography>
                        </Box>
                    </InfoCard>
                </Box>

                {/* Important Notice */}
                <InfoCard title="Important Notice" icon="info-circle">
                    <Typography sx={{ color: RED, fontWeight: 'bold', fontSize: isMobile ? "12px" : "14px", marginBottom: "10px" }}>
                        ACKNOWLEDGMENT OF POLICY
                    </Typography>
                    <Typography sx={{ color: BLACK_TEXT, fontSize: isMobile ? "12px" : "14px", lineHeight: "1.5" }}>
                        By proceeding with any purchase from Amar Enterprises Co Ltd, you explicitly acknowledge, understand, and agree to all terms outlined in this Return and Refund Policy. 
                        This includes the strict "No Claim, No Return" basis and "AS-IS, WHERE-IS" nature of all transactions.
                    </Typography>
                </InfoCard>

            </Box>
            
            {/* Space Filler for Footer Alignment */}
            <Box sx={{ height: isMobile ? "10px" : isTablet ? "10px" : "50px" }} />
            
            {/* Footer Component */}
            <FooterManual />
        </Box>
    );
};

export default ReturnsPolicy;